import Auth from '../auth'
import api from '@/services/api'
// import CSRF from '@/services/csrf'
import { apiErrorHandler } from '../utils/apiErrors'
import { objectToSnakeCase } from '../utils'
import store from '@/store/index'

const USERS_ENDPOINT = 'users/'
const USERS_ASSIGNMENTS_ENDPOINT = 'users/my-assignments/'
const PASSWORD_RESET_EMAIL_ENDPOINT = '/password/reset/'
const CHANGE_PASSWORD_ENDPOINT = '/password/reset/confirm/'
const LOGIN_ENDPOINT = 'login'

export default class UserAPI {
  /**
   * Instantiate a new `UserAPI`
   * @param {class} cls - The class to use to create objects.
   */
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `UserAPI`.
   * @param {class} cls - The class to use to create objects.
   **/
  static create(cls) {
    return new UserAPI(cls)
  }

  /* Get the API Client. */
  get client() {
    return api.getApiClient()
  }

  /**
   * Register a new user.
   *
   * @param {object} - Object to be destructured into local variables.
   * @param {object.string} email - Email of the new user.
   * @param {object.string} phoneNumber - Phone number of the new user
   * @param {object.string} password - Password of the new user.
   *
   * @returns {Promise} - Contains the API response data.
   */
  registration({ email, username, password, firstName, lastName, isMaker, phoneNumber }) {
    const url = USERS_ENDPOINT
    const data = {
      email,
      username,
      password,
      first_name: firstName,
      last_name: lastName,
      is_maker: isMaker,
      phone_number: phoneNumber,
    }

    return this.client
      .post(url, data)
      .then(response => response.data)
      .then(this.cls.fromAPI)
      .catch(apiErrorHandler({ apiName: 'Registration API error' }))
  }

  /**
   * Log in a user through the API.
   * Returns object containing user and token.
   *
   * @param {String} username
   * @param {String} password
   */
  login(username, password) {
    return (
      this.client
        .post(`${LOGIN_ENDPOINT}/`, { username, password })
        // Save token separately from user model, so that we don't have
        // to worry about messing with the token when we patch / copy the user.
        .then(response => ({
          token: response.data.token,
          user: this.cls.fromAPI(response.data),
        }))
        .catch(error => {
          throw error
        })
    )
  }

  /**
   * Get User's profile data via the API
   *
   * @param {string} idUser - ID of the user to retrieve.
   * @returns {Promise} - Promise wrapping the user data in API response.
   */
  getUser(idUser) {
    const url = `${USERS_ENDPOINT}${idUser}/`
    return this.client
      .get(url)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'Get User Profile Data API error' }))
  }

  /**
   * Update User's profile via the API.
   *
   * @param {User} user - User's account data as a `User` instance.
   * @param {Array} fields - Fields to include in a partial update.
   *                         If this list is empty, the entire user
   *                         object will be POSTed.
   * @param {Array} excludeFields - Fields to exclude from the update.
   *
   * @returns {Promise} - Contains the updated `User` instance
   **/
  update(user, fields = [], excludeFields = []) {
    const url = `${USERS_ENDPOINT}${user.id}/`
    let data = this.cls.toAPI(user, fields, excludeFields)

    return this.client
      .patch(url, data)
      .then(response => response.data)
      .then(this.cls.fromAPI)
      .catch(apiErrorHandler({ apiName: 'Update User Profile Data API error' }))
  }

  /**
   * Create a new user account.
   *
   * @param {User} user - User account data as `User` instance.
   * @returns {Promise} - Contains an object with the new `User` instance and a token.
   */
  create(user) {
    const url = USERS_ENDPOINT
    const data = objectToSnakeCase(user)
    return (
      this.client
        .post(url, data)
        // Save token separately from user model, so that we don't have
        // to worry about messing with the token when we patch / copy the user.
        .then(response => ({
          token: response.data.token,
          user: this.cls.fromAPI(response.data),
        }))
        .catch(error => {
          // eslint-disable-next-line no-console
          throw error
          // apiErrorHandler({ apiName: 'Create User API error' })
        })
    )
  }

  /**
   * Get List of Users via the API.
   *
   * @param {object} pagination - Pagination parameters for the API.
   * @param {object} filters - Filter parameters for the API.
   *
   * @returns {Promise} - Contains the list of `User` instances.
   */
  list({ filters = {} } = {}) {
    const url = USERS_ENDPOINT
    const options = {
      params: {
        ...filters,
      },
    }

    // NRS 7.2.19 -
    // In the past I have just assigned the filters to the params object one by one, doing something like this:
    //
    // if (filters.filteriName) {
    //   options.params.filter_name = filters.filterName
    // }
    //
    // Like I did below. I do this to avoid any issues with null values. So if the params objects
    // has something like { search: '' }  then by spreading it, I would get a URL that says {{url}}/?search=
    // I don't know if that causes issues with Django filters, but it is ugly! So instead, I just choose them
    // one by one
    //
    // This also allows me to manage the snake case to camel case issue between JS and DRF,
    // like in the example above.
    //
    // I don't know if this is being used elsewhere so I'll leave it for now.

    if (options.params.isMaker) {
      options.params.is_maker = options.params.isMaker
      delete options.params.isMaker
    }

    if (options.params.isActivator) {
      options.params.is_activator = options.params.isActivator
      delete options.params.isActivator
    }

    if (options.params.pageSize) {
      options.params.page_size = options.params.pageSize
      delete options.params.pageSize
    }

    return this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => ({
        ...data,
        results: data.results.map(this.cls.fromAPI),
      }))
      .catch(apiErrorHandler({ apiName: 'List Users API error' }))
  }

  listAssignments({ filters = {} } = {}) {
    const url = USERS_ASSIGNMENTS_ENDPOINT
    const options = {
      params: {},
    }

    if (filters.mNeedsApproval) {
      options.params.m_needs_approval = filters.mNeedsApproval
    }

    if (filters.search) {
      options.params.search = filters.search
    }

    if (filters.page) {
      options.params.page = filters.page
    }

    return this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => ({
        ...data,
        results: data.results.map(this.cls.fromAPI),
      }))
      .catch(apiErrorHandler({ apiName: 'List User Assignments API error' }))
  }

  /**
   * Upload User's profile image via the API.
   * @param {File} imageFile - User's imageFile to be uploaded.
   * @returns {Promise} - Contains the API response data.
   */

  async uploadProfilePic(userId, file) {
    const url = `${USERS_ENDPOINT}${userId}/profile-pic/`
    const options = {
      headers: {
        ...store.getters.getHeaders,
        'Content-Type': 'multipart/form-data',
      },
    }
    /* eslint-disable-next-line */
    const data = new FormData()
    data.append('profile_pic', file)

    return this.client
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'Upload User Profile Pic API error' }))
  }

  // Add activator to project
  addActivator(makerId, activatorId) {
    const url = `${USERS_ENDPOINT}${makerId}/assign-activator/`
    const authHeader = Auth.getAuthorizationHeader()
    const data = {
      activator: activatorId,
    }
    // TODO: All of these should be pulled out to the apiClient in the store
    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }
  // Remove activator to project
  removeActivator(makerId) {
    const url = `${USERS_ENDPOINT}${makerId}/remove-activator/`
    const authHeader = Auth.getAuthorizationHeader()
    // TODO: All of these should be pulled out to the apiClient in the store
    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  sendResetPasswordEmail(email) {
    const url = PASSWORD_RESET_EMAIL_ENDPOINT
    const authHeader = Auth.getAuthorizationHeader()
    const data = {
      email,
    }
    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  resetPassword(newPassword1, newPassword2, uid, token) {
    const url = CHANGE_PASSWORD_ENDPOINT
    const authHeader = Auth.getAuthorizationHeader()
    const data = {
      new_password1: newPassword1,
      new_password2: newPassword2,
      uid,
      token,
    }
    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, data, options)
      .then(response => this.cls.fromAPI(response.data))
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  // ------------------------
  // STRIPE FUNCTIONS
  // ------------------------

  addPaymentMethod(funderId, token) {
    const url = `${USERS_ENDPOINT}${funderId}/add-payment-method/`
    const authHeader = Auth.getAuthorizationHeader()
    const data = {
      token,
    }

    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, data, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  getPaymentMethods(funderId) {
    const url = `${USERS_ENDPOINT}${funderId}/view-payment-methods/`
    const authHeader = Auth.getAuthorizationHeader()

    const options = {
      headers: {
        ...authHeader,
      },
    }

    return this.client
      .get(url, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  updateDefaultPaymentMethod(funderId, cardId) {
    const url = `${USERS_ENDPOINT}${funderId}/update-default-payment-method/`
    const authHeader = Auth.getAuthorizationHeader()

    const data = {
      card_id: cardId,
    }

    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .post(url, data, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }

  getDefaultPaymentMethod(funderId) {
    const url = `${USERS_ENDPOINT}${funderId}/get-default-payment-method/`
    const authHeader = Auth.getAuthorizationHeader()

    const options = {
      headers: {
        ...authHeader,
      },
    }
    return this.client
      .get(url, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ apiName: 'API error' }))
  }
}
